import { useEffect, useState } from 'react';
import createAuthedAxios from './useAxiosAuth';
import baseURL from '../utils/baseURL';
import { readSession } from './useSession';
import { currencySymbol } from '../utils/currency';

// The currency THIS hospital charges in, for screens that price by the hospital
// rather than by one visit or one doctor: the admin dashboard's totals and the
// unified-billing counter (P24).
//
// Where it comes from. A tenant's country is fixed when Super Admin creates it
// and every doctor and admin inherits it; login hands it to the client as
// `TenantCountryId` (hooks/useSession.js). The country master row carries the
// currency (currencyCode / currencySymbol), and GET /country/:id is one of the
// master reads left open for every form. Nothing else the client holds names
// the hospital's currency — invoices, the billing orders and the dashboard
// snapshot do not.
//
// Fetched once per country per page load and shared by every caller. Resolves
// to null when the session has no tenant (super_admin), the country names no
// currency, or the read fails — the caller then shows the bare amount, never a
// guessed sign.

const cache = new Map(); // countryId -> Promise<{ currencyCode, currencySymbol } | null>

export const loadHospitalCurrency = (countryId) => {
  if (!countryId) return Promise.resolve(null);
  if (!cache.has(countryId)) {
    const pending = Promise.resolve()
      .then(() => createAuthedAxios().get(`${baseURL}/country/${encodeURIComponent(countryId)}`))
      .then((res) => {
        const country = res?.data?.country;
        if (!country || !currencySymbol(country)) return null;
        return { currencyCode: country.currencyCode || '', currencySymbol: country.currencySymbol || '' };
      })
      .catch(() => {
        // Let a later screen try again rather than remembering a blip.
        cache.delete(countryId);
        return null;
      });
    cache.set(countryId, pending);
  }
  return cache.get(countryId);
};

/** Test-only: forget every fetched country. */
export const resetHospitalCurrencyCache = () => cache.clear();

/**
 * The hospital's currency as `{ currencyCode, currencySymbol }`, or null until
 * (or unless) it is known.
 */
const useHospitalCurrency = () => {
  const { tenantCountryId } = readSession();
  const [currency, setCurrency] = useState(null);

  useEffect(() => {
    let alive = true;
    loadHospitalCurrency(tenantCountryId).then((found) => {
      if (alive) setCurrency(found);
    });
    return () => { alive = false; };
  }, [tenantCountryId]);

  return currency;
};

export default useHospitalCurrency;
